import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StatusPicker } from '@/components/apps/StatusPicker';
import { supabase } from '@/lib/supabase';
import { deleteAppFolder, placeInAppFolder } from '@/lib/app-collaboration';
import type { AppStatus } from '@/lib/app-records';
import type { DataTableCell, DataTableColumn } from '@shared/types/views';
import { cellText } from '@/components/apps/app-records-model';
import { FOLDER_VISIBILITY_LABELS } from '@/pages/FoldersPage';

type Item = {
  recordId: string;
  position: number;
  title: string;
  status: AppStatus;
  appKey: string;
  appTitle: string;
};

type ItemRow = {
  record_id: string;
  position: number;
  app_records: {
    computed: DataTableCell[];
    status: string;
    tool_apps: { key: string; title: string } | null;
    app_collections: { columns: DataTableColumn[]; statuses: AppStatus[] } | null;
  } | null;
};

/** One folder: its objects across apps, in the order people chose. */
export const FolderPage = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const [folder, setFolder] = useState<{ name: string; description: string | null; visibility: 'private' | 'team' | 'org'; canManage: boolean } | null>(null);
  const [items, setItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!folderId) return;
    const [{ data: folderRow }, { data: rows, error: itemsError }, { data: user }] = await Promise.all([
      supabase.from('app_folders').select('name, description, visibility, owner_user_id, tenant_id').eq('id', folderId).maybeSingle(),
      supabase.from('app_folder_items')
        .select('record_id, position, app_records(computed, status, tool_apps(key, title), app_collections(columns, statuses))')
        .eq('folder_id', folderId).order('position'),
      supabase.auth.getUser(),
    ]);
    if (!folderRow || itemsError) { setError('Ce dossier est introuvable ou inaccessible.'); return; }
    const { data: member } = await supabase.from('tenant_members').select('tenant_role').eq('user_id', user.user?.id ?? '').maybeSingle();
    setFolder({ name: folderRow.name, description: folderRow.description, visibility: folderRow.visibility,
      canManage: folderRow.owner_user_id === user.user?.id || member?.tenant_role === 'manager' });
    setItems(((rows ?? []) as unknown as ItemRow[]).filter((row) => row.app_records).map((row) => {
      const record = row.app_records!;
      const firstKey = record.app_collections?.columns[0]?.key;
      return {
        recordId: row.record_id, position: row.position,
        title: cellText(record.computed.find((cell) => cell.key === firstKey)) || 'Sans titre',
        status: record.app_collections?.statuses.find((status) => status.key === record.status) ?? { key: record.status, label: record.status, tone: 'neutral' },
        appKey: record.tool_apps?.key ?? '', appTitle: record.tool_apps?.title ?? '',
      };
    }));
  }, [folderId]);

  useEffect(() => { void reload(); }, [reload]);

  const act = async (action: () => Promise<void>) => {
    setError(null);
    try { await action(); await reload(); } catch (cause) { setError(cause instanceof Error ? cause.message : 'Le dossier n’a pas pu être modifié.'); }
  };
  // Moving swaps a neighbour's place: a position between its two neighbours.
  const move = (index: number, delta: -1 | 1) => {
    if (!items || !folderId) return;
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const after = items[target + (delta > 0 ? 1 : -1)];
    const position = after ? (items[target]!.position + after.position) / 2 : items[target]!.position + delta;
    void act(() => placeInAppFolder(folderId, items[index]!.recordId, { position }));
  };

  if (error && !folder) return <p role="alert" className="px-6 py-12 text-sm text-domino-inkSoft">{error}</p>;
  if (!folder || !items) return <p role="status" className="px-6 py-12 text-sm text-domino-inkSoft">Chargement du dossier…</p>;
  return (
    <div className="mx-auto max-w-4xl px-5 py-7 sm:px-8">
      <Link to="/folders" className="text-sm text-domino-inkSoft hover:text-domino-aubergine">Dossiers</Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[28px] font-semibold tracking-tight text-domino-ink">{folder.name}</h1>
          <p className="mt-1 text-sm text-domino-inkSoft">{folder.description ? `${folder.description} · ` : ''}{FOLDER_VISIBILITY_LABELS[folder.visibility]}</p>
        </div>
        {folder.canManage && (
          <Button variant="secondary" onClick={() => { if (window.confirm(`Supprimer le dossier « ${folder.name} » ? Les lignes restent dans leurs applications.`)) void deleteAppFolder(folderId!).then(() => navigate('/folders')); }}>
            Supprimer le dossier
          </Button>
        )}
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-[var(--domino-critical)]">{error}</p>}
      {items.length === 0 ? <p className="mt-8 text-sm text-domino-inkSoft">Ce dossier est vide. Depuis la fiche d’une ligne, « Ranger dans un dossier ».</p> : (
        <ol className="mt-6 rounded-2xl border border-domino-cardBorder bg-domino-white">
          {items.map((item, index) => (
            <li key={item.recordId} className="flex items-center gap-3 border-t border-domino-cardBorder px-4 py-3 first:border-t-0">
              <span className="w-6 text-right text-xs tabular-nums text-domino-inkSoft">{index + 1}</span>
              <Link to={`/apps/${encodeURIComponent(item.appKey)}?record=${item.recordId}`} className="min-w-0 flex-1 hover:text-domino-aubergine">
                <span className="block font-medium text-domino-ink">{item.title}</span>
                <span className="block text-xs text-domino-inkSoft">{item.appTitle}</span>
              </Link>
              <StatusPicker statuses={[item.status]} value={item.status.key} disabled onChange={() => {}} />
              <button type="button" aria-label={`Monter « ${item.title} »`} disabled={index === 0} onClick={() => move(index, -1)} className="rounded p-1 text-domino-inkSoft hover:bg-domino-muted disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" aria-label={`Descendre « ${item.title} »`} disabled={index === items.length - 1} onClick={() => move(index, 1)} className="rounded p-1 text-domino-inkSoft hover:bg-domino-muted disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" aria-label={`Retirer « ${item.title} » du dossier`} onClick={() => void act(() => placeInAppFolder(folderId!, item.recordId, { remove: true }))} className="rounded p-1 text-domino-inkSoft hover:bg-domino-muted"><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
